import { useNavigate } from '@tanstack/react-router'
import { Play, Calendar, User, Clock, Tag, Layers, Flame } from 'lucide-react'
import { showErrorData } from '@/lib/toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuthStore } from '@/features/auth/'
import { useEnginePlayActions } from '@/features/chats'
import { type Engine } from '../types/type'

interface SceneDetailProps {
  engine: Engine | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EngineDetail({ engine, open, onOpenChange }: SceneDetailProps) {
  const defaultCover = './default-cover/default.jpg'
  const navigate = useNavigate()
  const { startSession } = useEnginePlayActions()
  const { auth } = useAuthStore()

  if (!engine) return null

  const mainImage = engine.coverURL || engine.imageUrl || defaultCover
  const tags = Array.isArray(engine.tags) ? (engine.tags as string[]) : []
  const playCount = engine.statistics?.plays ?? 0

  const onClickStartSession = async () => {
    if (!auth?.user) {
      showErrorData('请先登录')
      return
    }
    const response = await startSession({
      engineId: engine.id,
    })
    const sessionId = response?.session?.id
    if (sessionId) {
      navigate({
        to: '/chats/$sessionId',
        params: { sessionId },
      })
    }
    onOpenChange(false)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '未知'
    try {
      return new Date(dateString).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return '未知'
    }
  }

  const formatPlayCount = (count: number) => {
    if (count >= 10000) {
      return `${(count / 10000).toFixed(1)}万`
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`
    }
    return count.toString()
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className='border-border bg-card h-[90vh] max-h-[90vh] gap-0 overflow-hidden p-0 data-[vaul-drawer-direction=bottom]:mt-4 data-[vaul-drawer-direction=bottom]:max-h-[90vh] sm:rounded-t-2xl'>
        <DrawerHeader className='sr-only'>
          <DrawerTitle>{engine.name || engine.title}</DrawerTitle>
          <DrawerDescription>{engine.description}</DrawerDescription>
        </DrawerHeader>

        <div className='flex h-full flex-col md:flex-row'>
          {/* 左侧：主图区域 */}
          <div className='relative h-48 w-full shrink-0 md:h-full md:w-[45%]'>
            {mainImage ? (
              <img
                src={mainImage}
                alt={engine.name || engine.title}
                className='h-full w-full object-cover'
              />
            ) : (
              <div className='bg-muted flex h-full w-full items-center justify-center'>
                <Layers className='text-muted-foreground h-16 w-16' />
              </div>
            )}
            <div className='from-card absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t to-transparent md:hidden' />
          </div>

          {/* 右侧：内容区域 */}
          <div className='bg-card flex flex-1 flex-col gap-4 overflow-hidden p-5 md:p-6'>
            <div className='flex min-h-0 flex-1 flex-col gap-2'>
              {/* 名称和版本 */}
              <div className='mb-4'>
                <div className='flex items-center gap-2'>
                  <h2 className='text-foreground text-xl font-bold md:text-2xl'>
                    {engine.name || engine.title || '未命名引擎'}
                  </h2>
                </div>
              </div>

              {/* 作者信息 */}
              <div className='text-muted-foreground mb-4 flex items-center gap-2 text-sm'>
                <User className='h-4 w-4' />
                <span>
                  作者：
                  <span className='text-foreground font-medium'>
                    {engine.author || '匿名'}
                  </span>
                </span>
              </div>

              {/* 描述区域，支持滚动 */}
              <ScrollArea className='min-h-0 flex-1'>
                {/* 标签 */}
                {tags.length > 0 && (
                  <div className='mb-4'>
                    <div className='text-muted-foreground mb-2 flex items-center gap-1.5 text-xs'>
                      <Tag className='h-3.5 w-3.5' />
                      <span>标签</span>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      {tags.map((tag, i) => (
                        <Badge
                          key={i}
                          variant='secondary'
                          className='bg-accent text-accent-foreground hover:bg-accent/80 px-2.5 py-0.5 text-xs transition-colors'
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <p className='text-muted-foreground text-sm leading-relaxed break-words whitespace-pre-wrap'>
                  {engine.description || '暂无描述'}
                </p>
              </ScrollArea>

              {/* 时间信息 */}
              <div className='text-muted-foreground mb-4 flex flex-wrap gap-x-6 gap-y-2 text-xs'>
                <div className='flex items-center gap-1.5'>
                  <Calendar className='h-3.5 w-3.5' />
                  <span>创建于 {formatDate(engine.createdAt)}</span>
                </div>
                <div className='flex items-center gap-1.5'>
                  <Clock className='h-3.5 w-3.5' />
                  <span>更新于 {formatDate(engine.updatedAt)}</span>
                </div>
              </div>
            </div>

            {/* 底部操作区 */}
            <div className=''>
              <div className='flex items-center gap-3'>
                <Button
                  onClick={onClickStartSession}
                  className='bg-primary text-primary-foreground hover:bg-primary/90 h-11 flex-1 text-base font-semibold shadow-md transition-all hover:shadow-lg'
                >
                  <Play className='mr-2 h-4 w-4 fill-current' />
                  开始游玩
                </Button>

                <div className='border-border bg-secondary/50 flex h-11 items-center justify-center gap-2 rounded-lg border px-4 text-sm'>
                  <Flame className='text-primary h-4 w-4' />
                  <div className='flex flex-col items-start'>
                    <span className='text-foreground font-semibold'>
                      {formatPlayCount(playCount)}
                    </span>
                    <span className='text-muted-foreground text-[10px]'>
                      热度
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
